import axios from 'axios';
import { generateEstimatesWithGemini } from './gemini';


// Create axios instance with default config
const api = axios.create({
   baseURL: 'http://127.0.0.1:8000/api',  // Direct URL to your Django backend
//  baseURL: 'https://triageapi.rawymo.com/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: false,
  timeout: 10000,  // 10 seconds timeout
});

// --- Auth tokens (in-memory) ---
let AUTH = { accessToken: null, refreshToken: null };
export const setAuthTokens = ({ accessToken, refreshToken }) => {
  AUTH.accessToken = accessToken || null;
  AUTH.refreshToken = refreshToken || null;
};

// Request interceptor — attach Authorization when available
api.interceptors.request.use(
  config => {
    if (AUTH.accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${AUTH.accessToken}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Token refresh handling
let isRefreshing = false;
let refreshQueue = [];
const processQueue = (error, token = null) => {
  refreshQueue.forEach(p => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token);
    }
  });
  refreshQueue = [];
};

// Response interceptor — attempt refresh once on 401
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;

    if (status === 401 && !originalRequest._retry) {
      if (!AUTH.refreshToken) {
        // No refresh token; let caller handle logout
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request until refresh completes
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const resp = await api.post('/auth/refresh/', { refresh: AUTH.refreshToken });
        const newAccess = resp?.data?.access || resp?.data?.access_token || resp?.access || resp?.access_token;
        const newRefresh = resp?.data?.refresh || resp?.data?.refresh_token || AUTH.refreshToken;
        if (newAccess) {
          // update in-memory tokens
          setAuthTokens({ accessToken: newAccess, refreshToken: newRefresh });
          processQueue(null, newAccess);
          isRefreshing = false;

          // retry original
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          return api(originalRequest);
        }
        throw new Error('Refresh response missing access token');
      } catch (refreshErr) {
        isRefreshing = false;
        processQueue(refreshErr, null);
        // Clear tokens to prevent loops
        setAuthTokens({ accessToken: null, refreshToken: null });
        return Promise.reject(refreshErr);
      }
    }

    // Log other errors for debugging
    if (error.response) {
      console.error('API Error - Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.response.config?.url,
        method: error.response.config?.method,
        data: error.response.data,
      });
    } else if (error.request) {
      console.error('API Error - No Response:', { message: error.message, request: error.request });
    } else {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Export API methods

// --- Auth ---
export const login = async ({ email, password }) => {
  const resp = await api.post('/auth/login/', { email, password });
  return resp.data; // expect { access, refresh }
};

export const register = async ({ first_name, last_name, email, phone, password }) => {
  const resp = await api.post('/auth/register/', { first_name, last_name, email, phone, password });
  return resp.data; // expect { id, email } or message
};

export const getMe = async () => {
  const resp = await api.get('/auth/me/');
  return resp.data;
};

export const refreshToken = async (refresh) => {
  const resp = await api.post('/auth/refresh/', { refresh });
  return resp.data; // { access }
};

// Password reset
export const requestPasswordReset = async (email) => {
  const resp = await api.post('/auth/password/reset/', { email });
  return resp.data; // { detail, uid?, token? }
};

export const confirmPasswordReset = async ({ uid, token, new_password }) => {
  const resp = await api.post('/auth/password/reset/confirm/', { uid, token, new_password });
  return resp.data; // { detail }
};

export const getAssets = async () => {
  const response = await api.get('/assets/');
  return response.data;
};

export const getAsset = async (id) => {
  const response = await api.get(`/assets/${id}/`);
  return response.data;
};

export const createAsset = async (assetData) => {
  try {
    console.log('Sending asset data to server:', JSON.stringify(assetData, null, 2));
    const response = await api.post('/assets/', assetData);
    return response.data;
  } catch (error) {
    console.error('Error creating asset:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });
    throw error;
  }
};

export const updateAsset = async (id, assetData) => {
  const response = await api.put(`/assets/${id}/`, assetData);
  return response.data;
};

export const deleteAsset = async (id) => {
  try {
    await api.delete(`/assets/${id}/`);
    return true;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Failed to delete asset';
    throw new Error(errorMessage);
  }
};

export const updateAssetStrategy = async (id, strategy) => {
  const response = await api.post(`/assets/${id}/update_strategy/`, { strategy });
  return response.data;
};

export const getAuditLogs = (assetId) => {
  return api.get(`/assets/${assetId}/audit_logs/`);
};

// AI Analysis APIs
export const analyzeAsset = async (assetId) => {
  try {
    const response = await api.post(`/assets/${assetId}/analyze_asset/`);
    return response.data;
  } catch (error) {
    console.error('Error analyzing asset:', error);
    throw error;
  }
};

export const suggestStrategy = async (assetId) => {
  try {
    const response = await api.post(`/assets/${assetId}/suggest_strategy/`);
    return response.data;
  } catch (error) {
    console.error('Error getting strategy suggestions:', error);
    throw error;
  }
};

// --- Materials AI estimation ---
export const estimateMaterials = async (assetId, hints = {}) => {
  try {
    const resp = await api.post(`/assets/${assetId}/estimate_materials/`, hints);
    return resp.data; // { compositions: [{name, percentage}], sources, confidence }
  } catch (error) {
    // Fallback: if endpoint not found (404/405), try generic estimator (local Gemini helper)
    if (error?.response && (error.response.status === 404 || error.response.status === 405)) {
      try {
        const payload = { assetId, hints, timestamp: new Date().toISOString() };
        const data = await generateEstimatesWithGemini(payload);
        return { compositions: data?.compositions || [], sources: data?.sources || [], confidence: data?.confidence || 0.7, isEstimate: true };
      } catch (e) {
        console.error('Local materials estimation failed:', e);
      }
    }
    console.error('estimateMaterials failed:', error);
    throw error;
  }
};

export const estimateMaterialsGeneric = async (payload = {}) => {
  // Generic path not tied to an existing asset (e.g., during creation before PUT)
  try {
    const data = await generateEstimatesWithGemini({ ...payload, timestamp: new Date().toISOString() });
    return { compositions: data?.compositions || [], sources: data?.sources || [], confidence: data?.confidence || 0.7, isEstimate: true };
  } catch (e) {
    console.error('estimateMaterialsGeneric failed:', e);
    return { compositions: [], sources: [], confidence: 0.0, isEstimate: true };
  }
};

// AI Market Analysis APIs
export const getMarketTrends = async ({ assetId, assetType, materialTypes = [] }) => {
  const response = await api.post('/ai/market-trends/', {
    asset_id: assetId,
    asset_type: assetType,
    material_types: materialTypes,
  });
  return response.data;
};

export const getAISuggestions = async (assetData) => {
  const response = await api.post('/ai/suggest-estimates/', {
    asset: assetData,
    timestamp: new Date().toISOString(),
  });
  return response.data;
};

// Smart suggestions: backend-only to ensure data is persisted before AI
export const getAISuggestionsSmart = async (assetData) => {
  return getAISuggestions(assetData);
};

// Material market data - using mock data since endpoint doesn't exist
// This data could be derived from asset materials using getAsset/getAssets in the future
export const getMaterialMarketData = async () => {
  const response = await api.get('/materials/market-data/');
  return response.data;
};

// Strategies - now served by backend
export const getStrategies = async () => {
  const response = await api.get('/strategies/');
  return response.data;
};

// Monitoring APIs
export const getMonitoringByAsset = async (assetId) => {
  const response = await api.get(`/monitoring/by-asset/${assetId}/`);
  return response.data;
};

export const createMonitoringEntry = async (assetId, data) => {
  // API expects POST /monitoring/ with body including asset id
  const payload = { asset: assetId, ...data };
  const response = await api.post('/monitoring/', payload);
  return response.data;
};

// --- Advanced Circularity & Compliance Services ---

// Digital Product Passport (DPP)
export const getAssetPassport = async (assetId) => {
  const response = await api.get(`/assets/${assetId}/passport/`);
  return response.data;
};

export const generateAssetPassport = async (assetId) => {
  // AUTH-TODO: attach Authorization header when auth is enabled
  const response = await api.post(`/assets/${assetId}/passport/generate/`);
  return response.data;
};

// --- Bulk import ---
export const bulkUploadAssets = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const resp = await api.post('/bulk/assets/upload/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
};

export const bulkFetchAssetsFromUrl = async (url) => {
  const resp = await api.post('/bulk/assets/fetch-url/', { url });
  return resp.data;
};

// --- Reports (PDF) ---
export const downloadAssetReport = async (assetId) => {
  const resp = await api.get(`/reports/asset/${assetId}.pdf`, { responseType: 'blob' });
  return resp.data;
};

export const downloadPortfolioReport = async () => {
  const resp = await api.get('/reports/portfolio.pdf', { responseType: 'blob' });
  return resp.data;
};

export const downloadEprReport = async () => {
  const resp = await api.get('/reports/epr.pdf', { responseType: 'blob' });
  return resp.data;
};

// Material Circularity Indicator (MCI)
export const getAssetMci = async (assetId) => {
  const response = await api.get(`/assets/${assetId}/mci/`);
  return response.data;
};

export const getPortfolioMci = async () => {
  const response = await api.get('/portfolio/mci/');
  return response.data;
};

// LCA-light estimator
export const estimateLCA = async ({ assetId }) => {
  const response = await api.post('/lca/estimate/', { asset_id: assetId });
  return response.data;
};

// Repairability & Remaining Useful Life (RUL)
export const getRepairability = async (assetId) => {
  const response = await api.get(`/assets/${assetId}/repairability/`);
  return response.data;
};

export const getRUL = async (assetId) => {
  const response = await api.get(`/assets/${assetId}/rul/`);
  return response.data;
};

// EPR/UPV summary (stub)
export const getEPRSummary = async ({ scheme, period }) => {
  const params = new URLSearchParams();
  if (scheme) params.append('scheme', scheme);
  if (period) params.append('period', period);
  const response = await api.get(`/epr/summary/?${params.toString()}`);
  return response.data;
};

// Compliance SVHC pre-check
export const checkSVHCCompliance = async (materials) => {
  const response = await api.post('/compliance/svhc/check/', { materials });
  return response.data;
};

// Reverse logistics optimization (stub)
export const optimizeLogistics = async (stops) => {
  const response = await api.post('/logistics/optimize/', { stops });
  return response.data;
};
